<template>
  <div class="single-slider-filter" data-testid="single-slider-filter">
    <Heading class="single-slider-filter__title" :level="6">
      {{ title }}
    </Heading>

    <div
      class="single-slider-filter__input-money"
      :class="{ 'is-at-max': isMaxValue }"
    >
      <InputMoney
        v-model="internalValue"
        :placeholder="placeholder"
        data-testid="input-money"
        @input="handleMoneyInput"
      />
    </div>

    <div class="single-slider-filter__input-range">
      <InputRange
        v-model="internalValue"
        :min="min"
        :max="max"
        :step="step"
        data-testid="input-range"
        @input="handleRangeChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Heading, InputMoney, InputRange } from '@warrenbrasil/nebraska-web';

interface Props {
  title: string;
  placeholder?: string;
  modelValue?: number;
  rangeValue?: number;
  min?: number;
  max?: number;
  step?: number;
}

interface Emits {
  (event: 'update:modelValue', value: number): void;
  (event: 'update:rangeValue', value: number): void;
  (event: 'change', payload: { money: number; range: number }): void;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'R$ 0,00',
  modelValue: 0,
  rangeValue: 0,
  min: 0,
  max: 100,
  step: 1
});

const emit = defineEmits<Emits>();

const internalValue = ref(props.modelValue || 0);
const isMaxValue = computed(() => internalValue.value >= props.max);

const inputMoneyValue = ref(props.modelValue);
const inputRangeValue = ref(props.rangeValue);

watch(
  () => props.modelValue,
  newValue => {
    inputMoneyValue.value = newValue;
  }
);

watch(
  () => props.rangeValue,
  newValue => {
    inputRangeValue.value = newValue;
  }
);

const handleMoneyInput = (value: number) => {
  inputMoneyValue.value = value;
  emit('update:modelValue', value);
  emit('change', {
    money: inputMoneyValue.value,
    range: inputRangeValue.value
  });
};

const handleRangeChange = (value: number) => {
  inputRangeValue.value = value;
  inputMoneyValue.value = value;

  emit('update:rangeValue', value);
  emit('update:modelValue', inputMoneyValue.value);
  emit('change', {
    money: inputMoneyValue.value,
    range: inputRangeValue.value
  });
};
</script>

<style lang="less" scoped>
.single-slider-filter {
  display: flex;
  flex-direction: column;
  gap: @size-spacing-x300;

  &__title {
    margin-top: @size-spacing-x500;
    color: @element-secondary;
  }

  &__input-money {
    width: 100%;
    border: 1px solid @divider-primary;
    border-radius: @size-radius-x300;
    padding: 0 @size-spacing-x500;
    position: relative;

    &.is-at-max::before {
      content: '+';
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      z-index: 2;
      font-weight: bold;
      color: inherit;
      pointer-events: none;
    }
  }

  &__input-range {
    width: 100%;
    margin: @size-spacing-x300 0 @size-spacing-x700 0;
  }
}
</style>
